import * as React from 'react'

import { Accordion, AccordionItem, AccordionHeader, AccordionPanel, Spinner, Text, makeStyles } from '@fluentui/react-components'
import { useMsal } from '@azure/msal-react'

import { getAccessToken } from '../../auth/msal'
import { fetchMessages } from '../../services/message'
import { mergeMessages } from '../../utils/message'

import EmailCard from '../components/EmailCard'
import { OFFSET_INCREMENT } from '../constants/search'
import accordionStyle from '../styles/accordion'
import emailCardStyle from '../styles/email-card'

const { cardList } = emailCardStyle

const useStyles = makeStyles({
  ...accordionStyle,
  cardList
})

const EmailCardsContainer = (props) => {
  const {
    messages,
    setMessages,
    searchParams,
    setSearchParams,
    hasMoreMessages,
    setHasMoreMessages
  } = props

  const {
    k2Id,
    sortBy
  } = searchParams

  const classes = useStyles()

  const [loading, setLoading] = React.useState(false)

  const observer = React.useRef()

  const { instance } = useMsal()

  const loadMessages = React.useCallback(async () => {
    setLoading(true)

    if (!k2Id) {
      setMessages([])
      setHasMoreMessages(false)
      setLoading(false)
      return
    }

    const searchBy = window.Office?.context?.mailbox?.userProfile?.emailAddress

    let response

    try {
      const accessToken = await getAccessToken(instance)

      response = await fetchMessages(accessToken, {
        searchBy,
        ...searchParams,
        offset: searchParams.offset + OFFSET_INCREMENT
      })
    } catch (error) {
      console.error('Error fetching messages:', error)
    }

    if (response.data.length === 0) {
      setHasMoreMessages(false)
      setLoading(false)
      return
    }

    setMessages((previousMessages) => mergeMessages(previousMessages, response.data, sortBy))

    setLoading(false)
  }, [searchParams.offset])

  const lastMessageElementRef = React.useCallback((node) => {
    if (loading || !hasMoreMessages) return

    if (observer.current) observer.current.disconnect()

    observer.current = new window.IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setSearchParams((prevParams) => ({
          ...prevParams,
          offset: prevParams.offset + OFFSET_INCREMENT
        }))
      }
    }, { threshold: 0.1 })

    if (node) observer.current.observe(node)
  }, [loading, hasMoreMessages])

  React.useEffect(() => {
    if (hasMoreMessages) {
      loadMessages()
    }
  }, [loadMessages, hasMoreMessages])

  return (
    <Accordion multiple collapsible defaultOpenItems={Array.from({ length: 100 }, (_, i) => `${i + 1}`)} className={classes.accordion}>
      {messages.map((message, messageIndex) => (
        <AccordionItem
          key={messageIndex}
          value={(messageIndex + 1).toString()}
        >
          <AccordionHeader>
            <Text weight='semibold'>{message[sortBy]}</Text>
          </AccordionHeader>
          <AccordionPanel className={classes.accordionPanel}>
            <div className={classes.cardList}>
              {message.emails.map((email, emailIndex) => {
                const isLastCard = messageIndex === messages.length - 1 && emailIndex === message.emails.length - 1

                return (
                  <EmailCard
                    key={email.id}
                    email={email}
                    emailIndex={emailIndex}
                    isLastCard={isLastCard}
                    lastMessageElementRef={isLastCard ? lastMessageElementRef : null}
                    messageIndex={messageIndex}
                  />
                )
              })}
            </div>
          </AccordionPanel>
        </AccordionItem>
      ))}
      {loading && <Spinner size='large' />}
    </Accordion>
  )
}

export default EmailCardsContainer
